import * as React from 'react';

import { cn } from '../../utils/cn';

export interface StyleTileOption {
    value: string;
    /** Set uppercase over the photo, e.g. `MINIMALIST`. */
    name: string;
    /** The line under it, e.g. `Clean · Pure`. */
    caption?: string;
    image?: string;
}

export interface StyleTileGridProps {
    options: StyleTileOption[];
    values: string[];
    onChange: (values: string[]) => void;
    /** Accessible name for the grid. */
    label: string;
    /** Refuses further selections past this many. Unlimited when omitted. */
    max?: number;
    className?: string;
}

/**
 * The visual multi-select: a photo grid where the picture is the question.
 *
 * Selection darkens the tile rather than adding a tick, because a tick would
 * sit on photography that is itself the thing being judged. The scrim is
 * carried on a sibling layer so it can cross-fade without the image repainting.
 */
export const StyleTileGrid = ({
    options,
    values,
    onChange,
    label,
    max,
    className,
}: StyleTileGridProps) => {
    const toggle = (value: string) => {
        if (values.includes(value)) {
            onChange(values.filter((entry) => entry !== value));
            return;
        }
        if (max !== undefined && values.length >= max) return;
        onChange([...values, value]);
    };

    return (
        <div
            aria-label={label}
            className={cn('grid grid-cols-2 gap-2.5 md:grid-cols-3', className)}
            role="group"
        >
            {options.map((option) => {
                const checked = values.includes(option.value);
                const atLimit =
                    !checked && max !== undefined && values.length >= max;

                return (
                    <button
                        aria-checked={checked}
                        aria-disabled={atLimit || undefined}
                        className={cn(
                            'group relative aspect-[240/210] overflow-hidden rounded-3 bg-[#f6f6f6] text-left',
                            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#181818]',
                            atLimit && 'cursor-not-allowed opacity-50',
                        )}
                        key={option.value}
                        onClick={() => toggle(option.value)}
                        role="checkbox"
                        type="button"
                    >
                        {option.image ? (
                            <img
                                alt=""
                                className="absolute inset-0 size-full object-cover"
                                src={option.image}
                            />
                        ) : null}

                        <span
                            aria-hidden="true"
                            className={cn(
                                'absolute inset-0 transition-colors',
                                checked
                                    ? 'bg-black/45'
                                    : 'bg-gradient-to-t from-black/55 via-black/10 to-transparent group-hover:from-black/65',
                            )}
                        />

                        <span className="absolute inset-x-3.5 bottom-3.5 flex flex-col gap-1">
                            <span className="font-sans text-3.25 leading-none font-medium tracking-[0.03em] text-white uppercase">
                                {option.name}
                            </span>
                            {option.caption ? (
                                <span className="font-sans text-3 leading-none tracking-[-0.01em] text-white/80">
                                    {option.caption}
                                </span>
                            ) : null}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};
